function pickString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

function normalizePersonName(value: string | null | undefined): string {
  return value?.trim().toLowerCase() ?? '';
}

/** `GET /profile` and worker rows for role_id 3 (business worker). */
export function isBusinessWorkerProfileRecord(record: Record<string, unknown> | null | undefined): boolean {
  if (!record) {
    return false;
  }
  const role = record.role_id ?? record.roleId;
  return role === 3 || role === '3';
}

function normalizeBusinessId(value: string | null | undefined): string {
  return value?.trim().toLowerCase() ?? '';
}

/**
 * GET /profile/workers echoes business-client contact on each flat row.
 * When first/last match contact_first/last on the same row, they are not worker identity.
 *
 * When contact_* is omitted, role_id 3 rows that carry employer branding still echo the
 * employer contact on flat first/last (worker `email` differs from row `business_id`).
 */
export function workerRowEchoesEmployerContact(record: Record<string, unknown>): boolean {
  const first = pickString(record.first_name, record.firstName);
  const last = pickString(record.last_name, record.lastName);
  const contactFirst = pickString(record.contact_first_name, record.contactFirstName);
  const contactLast = pickString(record.contact_last_name, record.contactLastName);
  if (first && last && contactFirst && contactLast) {
    if (
      normalizePersonName(first) === normalizePersonName(contactFirst) &&
      normalizePersonName(last) === normalizePersonName(contactLast)
    ) {
      return true;
    }
  }

  if (!isBusinessWorkerProfileRecord(record) || !first || !last) {
    return false;
  }

  const workerEmail = pickString(record.email, record.Email);
  const businessId = pickString(record.business_id, record.businessId);
  const hasEmployerBranding = Boolean(
    pickString(record.business_client_id, record.businessClientId) ||
      pickString(record.business_name, record.businessName) ||
      pickString(record.logo, record.logo_url, record.logoUrl) ||
      contactFirst ||
      contactLast,
  );
  if (!hasEmployerBranding || !workerEmail || !businessId) {
    return false;
  }
  if (normalizeBusinessId(workerEmail) === normalizeBusinessId(businessId)) {
    return false;
  }
  if (contactFirst && contactLast) {
    return (
      normalizePersonName(first) === normalizePersonName(contactFirst) &&
      normalizePersonName(last) === normalizePersonName(contactLast)
    );
  }
  return true;
}

/** Use worker `first_name`/`last_name` — not business-client `contact_*` on role_id 3 profiles. */
export function shouldUseWorkerPersonalNameFields(record: Record<string, unknown>): boolean {
  return isBusinessWorkerProfileRecord(record);
}

/** Treat placeholder API values as empty for display. */
export function meaningfulWorkerField(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }
  const upper = trimmed.toUpperCase();
  if (upper === 'N/A' || upper === 'NA' || upper === '-' || upper === 'NONE') {
    return null;
  }
  return trimmed;
}

/** Business owner branding echoed on worker rows — not a worker photo. */
export function isOwnerLogoImageUrl(url: string | null | undefined): boolean {
  const trimmed = url?.trim();
  if (!trimmed) {
    return false;
  }
  return /owner-logos/i.test(trimmed);
}

export function fallbackWorkerDisplayName(email: string | null | undefined): string {
  const trimmed = email?.trim();
  if (!trimmed) {
    return '—';
  }
  const local = trimmed.split('@')[0]?.trim() ?? '';
  if (local.length >= 2 && /[a-zA-Z\u0590-\u05FF]/.test(local)) {
    return local
      .replace(/[._-]+/g, ' ')
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }
  return trimmed;
}
